import { MouseEvent, useState } from 'react';

import { getFormalOfferInterestOnlyPeriod } from '@helpers/FormalOffers/getFormalOfferInterestOnlyPeriod';
import { getIsInterestOnlyOffer } from '@helpers/FormalOffers/getIsInterestOnlyOffer';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { IconAngleRight } from '@iwoca/icons';
import { fetchGetDrawDownRepaymentScheduleCreditFacility } from '@iwoca/lapi-client/edge';
import { Button, Modal, Pill } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import LoadingCalculationValue from '../../../multiLoanDashboard/dashboardComponents/loadingDashboardValue/LoadingDashboardValue';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import { VariableRate } from '../../../ui/rate';
import {
    getInterestOnlySchedule,
    getNonInterestOnlyPaymentAmount
} from '../../MyOffers.helpers';
import { DrawdownRepaymentSchedule, FormalOffer } from '../../MyOffers.types';
import EarlyRepaymentCalculator from '../earlyRepaymentCalculator/EarlyRepayment';
import { getMaxDate } from '../earlyRepaymentCalculator/EarlyRepaymentHelper';
import { InterestOnlyPaymentsDetails } from '../flexiApprovedView/interestOnly/InterestOnlyPaymentsAccordion';
import RepaymentScheduleModal from '../repaymentScheduleModal/RepaymentScheduleModal';
import { useGetDiscountedPrice } from '../useGetDiscountedPrice';

export const RecommendedOfferDetails = ({
    drawdownAmount,
    recommendedOffer,
    loanDuration,
    hideEarlyRepaymentLink
}: {
    drawdownAmount: number;
    recommendedOffer: FormalOffer;
    loanDuration: number;
    hideEarlyRepaymentLink?: boolean;
}) => {
    const stateKey = useStateKey();

    const { hasDiscountedPricing } = useGetDiscountedPrice({
        enabled: getCountry() === 'DE',
        formalOffer: recommendedOffer
    });

    const maxDate = getMaxDate(recommendedOffer.duration!.amount);

    const [isEarlyRepaymentModalOpen, setIsEarlyRepaymentModalOpen] =
        useState(false);

    const [isRepaymentScheduleModalOpen, setIsRepaymentScheduleModalOpen] =
        useState(false);

    const [isInterestRepaymentModalOpen, setIsInterestRepaymentModalOpen] =
        useState(false);

    const { isLoading, data: drawdownSchedule } = useQuery({
        queryKey: [
            'fetchGetDrawDownRepaymentScheduleCreditFacility',
            stateKey,
            drawdownAmount,
            recommendedOffer.offer_id
        ],
        queryFn: () =>
            fetchGetDrawDownRepaymentScheduleCreditFacility({
                stateKey: stateKey,
                offerId: recommendedOffer.offer_id!,
                query: { amount: drawdownAmount }
            })
    });

    const handleCloseEarlyRepaymentModal = () => {
        setIsEarlyRepaymentModalOpen(!isEarlyRepaymentModalOpen);
    };
    const handleCloseRepaymentScheduleModal = () => {
        setIsRepaymentScheduleModalOpen(!isRepaymentScheduleModalOpen);
    };
    const handleCloseInterestRepaymentModal = () => {
        setIsInterestRepaymentModalOpen(!isInterestRepaymentModalOpen);
    };

    let repaymentSchedule: DrawdownRepaymentSchedule['schedule'] | null = null;
    let scheduleSummary: DrawdownRepaymentSchedule['summary'];
    let equalMonthlyAmount = 0;
    let totalBalance = 0;
    let totalFees = 0;

    const offerRate = recommendedOffer.interest_rates![0].rate!;
    const offerRateGTZero = offerRate > 0;

    if (drawdownSchedule?.data) {
        repaymentSchedule = drawdownSchedule.data.schedule;
        scheduleSummary = drawdownSchedule.data.summary;
        // We assume that the !is_equal_repayment means the schedule is for interest-only
        equalMonthlyAmount = scheduleSummary.is_equal_repayment
            ? scheduleSummary.simplified_schedule!.equal_repayments!.amount
            : getNonInterestOnlyPaymentAmount(repaymentSchedule!);
        totalBalance = scheduleSummary!.total_payable!;
        totalFees = scheduleSummary!.total_fees!;
    }

    return (
        <>
            <div className="w-[500px] px-xl py-m max-sm:w-full max-sm:rounded-s max-sm:border-none max-sm:bg-structure-100">
                <h2 className="text-left">
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.heading"
                        defaultMessage="Your offer details"
                    />
                </h2>
                {hasDiscountedPricing && (
                    <div className="-mb-s text-right">
                        <Pill>
                            <FormattedMessage
                                id="_.recommended_offer_view.offer_details.discountedOffer"
                                defaultMessage="Our offer for you:"
                            />
                        </Pill>
                    </div>
                )}

                {offerRateGTZero ? (
                    <>
                        <div className="mt-l grid grid-cols-2 gap-l font-reg">
                            <FormattedMessage
                                id="_.recommended_offer_view.offer_details.withdrawal_fee"
                                defaultMessage="Withdrawal fee"
                            />
                            <LoadingCalculationValue isLoading={isLoading}>
                                <div className="text-right font-med">
                                    <VariableRate
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={2}
                                        value={
                                            recommendedOffer.withdrawal_fee_percentage ||
                                            0
                                        }
                                    />
                                </div>
                            </LoadingCalculationValue>
                        </div>

                        <div className="mt-l grid grid-cols-2 gap-l font-reg">
                            <FormattedMessage
                                id="_.recommended_offer_view.offer_details.interest_rate"
                                defaultMessage="Interest Rate"
                            />

                            <LoadingCalculationValue isLoading={isLoading}>
                                <div className="text-right font-med">
                                    {hasDiscountedPricing && (
                                        <>
                                            <span className="font-reg line-through">
                                                2,50&nbsp;%
                                            </span>{' '}
                                        </>
                                    )}
                                    <VariableRate
                                        minimumFractionDigits={2}
                                        maximumFractionDigits={3}
                                        value={offerRate}
                                        className="text-s font-med"
                                    />
                                </div>
                            </LoadingCalculationValue>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="mt-l grid grid-cols-2 gap-l font-reg">
                            <FormattedMessage
                                id="_.recommended_offer_view.offer_details.withdrawal_fee"
                                defaultMessage="Withdrawal fee"
                            />
                            <LoadingCalculationValue isLoading={isLoading}>
                                <div className="text-right">
                                    <div className="font-med">
                                        <MoneyWithDecimals value={totalFees} />
                                    </div>
                                    <div className="text-sm text-structure-400">
                                        <VariableRate
                                            minimumFractionDigits={2}
                                            maximumFractionDigits={2}
                                            value={
                                                totalFees /
                                                    (totalBalance -
                                                        totalFees) || 0
                                            }
                                        />{' '}
                                        <FormattedMessage
                                            id="_.recommended_offer_view.offer_details.fee_proportion"
                                            defaultMessage="of your withdrawal"
                                        />
                                    </div>
                                </div>
                            </LoadingCalculationValue>
                        </div>

                        <div className="mt-l grid grid-cols-2 gap-l font-reg">
                            <FormattedMessage
                                id="_.recommended_offer_view.offer_details.interest"
                                defaultMessage="Interest Charges"
                            />
                            <LoadingCalculationValue isLoading={isLoading}>
                                <div className="text-right font-med">
                                    <VariableRate
                                        minimumFractionDigits={0}
                                        maximumFractionDigits={3}
                                        value={offerRate}
                                        className="text-s font-med"
                                    />
                                </div>
                            </LoadingCalculationValue>
                        </div>
                    </>
                )}

                {getIsInterestOnlyOffer(recommendedOffer) && (
                    <div className="mt-l grid grid-cols-2 gap-l font-reg">
                        <FormattedMessage
                            id="_.recommended_offer_view.interest_only.repayment_breakdown.label"
                            defaultMessage="Interest-only repayments (months 1 - {interestOnlyPeriod})"
                            values={{
                                interestOnlyPeriod:
                                    getFormalOfferInterestOnlyPeriod(
                                        recommendedOffer
                                    )
                            }}
                        />

                        <div>
                            <Button
                                variant="tertiary"
                                className="!px-[0] !py-0 text-left min-sm:!-mt-s"
                                onClick={(e: MouseEvent) => {
                                    e.preventDefault();
                                    setIsInterestRepaymentModalOpen(true);
                                }}
                                size="small"
                            >
                                <FormattedMessage
                                    id="_.recommended_offer_view.interest_only.repayment_breakdown.show_details"
                                    defaultMessage="show details"
                                />
                                <IconAngleRight className="fill-primary-40" />
                            </Button>
                        </div>
                    </div>
                )}

                <div className="mt-l grid grid-cols-2 gap-l font-reg">
                    <div>
                        {getIsInterestOnlyOffer(recommendedOffer) ? (
                            <FormattedMessage
                                id="_.draw_down.choose_amount.interest_only_monthly_repayment"
                                defaultMessage="Minimum monthly repayment (months {monthlyPaymentStart} - {monthlyPaymentEnd})"
                                values={{
                                    monthlyPaymentStart:
                                        getFormalOfferInterestOnlyPeriod(
                                            recommendedOffer
                                        )! + 1,
                                    monthlyPaymentEnd: getDurationInMonths(
                                        recommendedOffer.duration!
                                    )
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="_.recommended_offer_view.offer_details.monthly_repayment"
                                defaultMessage="Monthly repayment"
                            />
                        )}

                        <div className="col-span-2">
                            <Button
                                variant="tertiary"
                                className="!px-[0] text-left min-sm:!-mt-xl"
                                size="small"
                                onClick={(e: MouseEvent) => {
                                    e.preventDefault();
                                    setIsRepaymentScheduleModalOpen(true);
                                }}
                            >
                                <FormattedMessage
                                    id="_.recommended_offer_view.offer_details.repayment_schedule_modal"
                                    defaultMessage="See my repayment schedule"
                                />
                                <IconAngleRight className="fill-primary-40" />
                            </Button>
                        </div>
                    </div>

                    <LoadingCalculationValue isLoading={isLoading}>
                        <div
                            data-testId="equal-monthly-amount"
                            className="text-right font-med"
                        >
                            <MoneyWithDecimals value={equalMonthlyAmount} />
                        </div>
                    </LoadingCalculationValue>
                </div>

                <div className="mt-l grid grid-cols-2 gap-l font-reg">
                    <FormattedMessage
                        id="_.recommended_offer_view.offer_details.total_repayment"
                        defaultMessage="Total repayment"
                    />

                    <LoadingCalculationValue isLoading={isLoading}>
                        <div
                            data-testId="total-repayment-amount"
                            className="text-right font-med"
                        >
                            <MoneyWithDecimals value={totalBalance} />
                        </div>
                    </LoadingCalculationValue>
                </div>
                {!hideEarlyRepaymentLink && (
                    <Button
                        variant="tertiary"
                        className="m-0 !px-[0] !py-0 text-left"
                        onClick={(e: MouseEvent) => {
                            e.preventDefault();
                            setIsEarlyRepaymentModalOpen(true);
                        }}
                        size="small"
                    >
                        <FormattedMessage
                            id="_.recommended_offer_view.offer_details.savings_calculator_modal"
                            defaultMessage="Save by repaying early"
                        />
                        <IconAngleRight className="fill-primary-40" />
                    </Button>
                )}
            </div>
            <Modal
                size="large"
                isOpen={isEarlyRepaymentModalOpen}
                onClose={handleCloseEarlyRepaymentModal}
                closeIcon
            >
                <EarlyRepaymentCalculator
                    drawdownAmount={drawdownAmount}
                    offerId={recommendedOffer.offer_id!}
                    loanDuration={loanDuration}
                    productType={recommendedOffer.product_type!}
                    maxDate={maxDate}
                />
            </Modal>
            <Modal
                size="large"
                isOpen={isRepaymentScheduleModalOpen}
                onClose={handleCloseRepaymentScheduleModal}
                closeIcon
            >
                <RepaymentScheduleModal
                    offerId={recommendedOffer?.offer_id!}
                    drawdownAmount={drawdownAmount}
                />
            </Modal>
            <Modal
                size="medium"
                isOpen={isInterestRepaymentModalOpen}
                onClose={handleCloseInterestRepaymentModal}
                closeIcon
            >
                <div className="mt-2xl">
                    <InterestOnlyPaymentsDetails
                        interestOnlyPeriod={
                            recommendedOffer.interest_only_repayments!
                        }
                        interestOnlySchedule={
                            getInterestOnlySchedule(repaymentSchedule)!
                        }
                    />
                </div>
            </Modal>
        </>
    );
};
